import { LayerKey } from './LayerKey'

export class OperationLayerMap<T> {
  private readonly map: Map<string, T>

  constructor(entries?: readonly (readonly [LayerKey, T])[] | null) {
    this.map = new Map()

    if (entries) {
      for (const [key, value] of entries) {
        this.set(key, value)
      }
    }
  }

  private serializeKey(key: LayerKey): string {
    return key.toString()
  }

  private deserializeKey(serializedKey: string): LayerKey {
    return new LayerKey(serializedKey)
  }

  set(key: LayerKey, value: T): this {
    const serializedKey = this.serializeKey(key)
    this.map.set(serializedKey, value)
    return this
  }

  get(key: LayerKey): T | undefined {
    const serializedKey = this.serializeKey(key)
    return this.map.get(serializedKey)
  }

  has(key: LayerKey): boolean {
    const serializedKey = this.serializeKey(key)
    return this.map.has(serializedKey)
  }

  delete(key: LayerKey): boolean {
    const serializedKey = this.serializeKey(key)
    return this.map.delete(serializedKey)
  }

  clear(): void {
    this.map.clear()
  }

  *entries(): IterableIterator<[LayerKey, T]> {
    for (const [serializedKey, value] of this.map.entries()) {
      const key = this.deserializeKey(serializedKey)
      if (key) {
        yield [key, value]
      }
    }
  }

  values(): IterableIterator<T> {
    return this.map.values()
  }

  *keys(): IterableIterator<LayerKey> {
    for (const serializedKey of this.map.keys()) {
      const key = this.deserializeKey(serializedKey)
      if (key) {
        yield key
      }
    }
  }

  get size(): number {
    return this.map.size
  }

  isEmpty(): boolean {
    return this.map.size === 0
  }

  forEach(
    // oxlint-disable-next-line max-params
    callbackfn: (value: T, key: LayerKey, map: OperationLayerMap<T>) => void,
    thisArg?: unknown,
  ): void {
    for (const [key, value] of this.entries()) {
      callbackfn.call(thisArg, value, key, this)
    }
  }
}
